import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import ComponentTypeSection from './ComponentTypeSection';
import { TemplatesByType, ComponentLifetime } from './types';
import { BaseType, BuildComponentTypeDefinition } from "../../../types/Enums";
import { translate } from "../../../types/EnumTranslations";

interface BaseTypeSectionProps {
    baseType: BaseType;
    templates: TemplatesByType;
    setTemplates: React.Dispatch<React.SetStateAction<TemplatesByType>>;
    componentLifetimes: Record<string, ComponentLifetime>;
    setComponentLifetimes: React.Dispatch<React.SetStateAction<Record<string, ComponentLifetime>>>;
    expandedBaseSections: Set<string>;
    expandedComponentSections: Set<string>;
    editingTemplate: string | null;
    editingLifetime: string | null;
    saving: boolean;
    toggleBaseSection: (baseType: string) => void;
    toggleComponentSection: (componentType: string) => void;
    setEditingTemplate: React.Dispatch<React.SetStateAction<string | null>>;
    setEditingLifetime: React.Dispatch<React.SetStateAction<string | null>>;
    setSaving: React.Dispatch<React.SetStateAction<boolean>>;
    getBaseTypeTemplateCount: (baseType: BaseType) => number;
    buildComponentTypes: Record<string, BuildComponentTypeDefinition>;
}

const BaseTypeSection: React.FC<BaseTypeSectionProps> = ({
    baseType,
    templates,
    setTemplates,
    componentLifetimes,
    setComponentLifetimes,
    expandedBaseSections,
    expandedComponentSections,
    editingTemplate,
    editingLifetime,
    saving,
    toggleBaseSection,
    toggleComponentSection,
    setEditingTemplate,
    setEditingLifetime,
    setSaving,
    getBaseTypeTemplateCount,
    buildComponentTypes,
}) => {
    const componentTypes = Object.entries(buildComponentTypes)
        .filter(([_, def]) => def.baseType === baseType)
        .map(([type]) => type);
    const isBaseExpanded = expandedBaseSections.has(baseType);
    const totalTemplateCount = getBaseTypeTemplateCount(baseType);

    return (
        <div className="accordion-item mb-3" >
            {/* Base Type Header */}
            <h2 className="accordion-header" >
                <button
                    className={`accordion-button ${!isBaseExpanded ? 'collapsed' : ''} base-type-header`}
                    type="button"
                    onClick={() => toggleBaseSection(baseType)}
                >
                    <div className="d-flex align-items-center justify-content-between w-100" >
                        <div className="d-flex align-items-center">
                            {isBaseExpanded ? (
                                <ChevronDown className="me-3" size={20} />
                            ) : (
                                <ChevronRight className="me-3" size={20} />
                            )}
                            <span>{translate(baseType)}</span>
                        </div>
                        <span className="badge bg-secondary bg-opacity-10 text-secondary ms-2 me-3">
                            {totalTemplateCount} mallar
                        </span>
                    </div>
                </button>
            </h2>

            {/* Component Types within Base Type */}
            {isBaseExpanded && (
                <div className="accordion-body p-0">
                    <div className="ps-4 pe-3 py-3">
                        {componentTypes.map((componentType: string) => (
                            <ComponentTypeSection
                                key={componentType}
                                componentType={componentType}
                                templates={templates}
                                setTemplates={setTemplates}
                                componentLifetimes={componentLifetimes}
                                setComponentLifetimes={setComponentLifetimes}
                                expandedComponentSections={expandedComponentSections}
                                editingTemplate={editingTemplate}
                                editingLifetime={editingLifetime}
                                saving={saving}
                                toggleComponentSection={toggleComponentSection}
                                setEditingTemplate={setEditingTemplate}
                                setEditingLifetime={setEditingLifetime}
                                setSaving={setSaving}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BaseTypeSection;